/* eslint-disable linebreak-style */
import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  colors,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Button
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import Loader from 'react-loader-spinner';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { strapi } from '../../../../strapi/strapi.config';
import { selectCurrentUser } from '../../../../redux/user/user.selectors';
import { selectLookupFirmeComisionar } from '../../../../redux/lookup/lookup.selectors';
import { t } from '../../../../i18n';

const useStyles = makeStyles(() => ({
  header: {
    background: colors.blueGrey[400]
  },
  button: {
    background: colors.blueGrey[500]
  }
}));

const pdfStyles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 9
  },
  title: {
    fontSize: 14,
    marginBottom: 8,
    fontWeight: 700
  },
  subtitle: {
    marginBottom: 10
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    borderBottomStyle: 'solid',
    paddingVertical: 4
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#222',
    borderBottomStyle: 'solid',
    backgroundColor: '#f0f0f0',
    paddingVertical: 4,
    fontWeight: 700
  },
  group: {
    marginBottom: 14
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
    color: '#1a237e'
  },
  totalRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#222',
    borderTopStyle: 'solid',
    backgroundColor: '#e8eaf6',
    paddingVertical: 4,
    fontWeight: 700
  },
  grandGroup: {
    marginTop: 10
  },
  grandTotalRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#222',
    borderStyle: 'solid',
    backgroundColor: '#c5cae9',
    paddingVertical: 5,
    fontWeight: 700
  },
  cTip: { width: '10%' },
  cId: { width: '8%' },
  cData: { width: '9%' },
  cClient: { width: '16%' },
  cVal: { width: '10%', textAlign: 'right' },
  cValWide: { width: '11%', textAlign: 'right' },
  cObs: { width: '11%', paddingLeft: 4 },
  cObsValue: { width: '11%', fontSize: 7, paddingLeft: 4 },
  cMark: { width: '4%', textAlign: 'center', fontSize: 7 },
  cMarkActive: {
    width: '4%',
    textAlign: 'center',
    fontSize: 7,
    fontWeight: 700,
    backgroundColor: '#fff176',
    color: '#1a237e',
    paddingVertical: 2
  },
  cMarkHeader: { width: '4%', textAlign: 'center', fontWeight: 700 },
  markedTotalRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#666',
    borderTopStyle: 'dashed',
    backgroundColor: '#fff8e1',
    paddingVertical: 4,
    fontWeight: 700
  }
});

const pad2 = (v) => String(v).padStart(2, '0');

const formatDateRO = (value) => {
  if (!value) {
    return '-';
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const getTodayRO = () => {
  const d = new Date();
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const getMonthStartRO = () => {
  const d = new Date();
  return `${pad2(1)}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const parseRoDate = (value, endOfDay = false) => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(value || '').trim());
  if (!match) {
    return null;
  }
  const zi = Number(match[1]);
  const luna = Number(match[2]);
  const an = Number(match[3]);
  const d = new Date(an, luna - 1, zi, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  if (d.getFullYear() !== an || d.getMonth() !== luna - 1 || d.getDate() !== zi) {
    return null;
  }
  return d;
};

const formatNumber = (value) => Number(value || 0).toFixed(2);

const EURO_TIPS = ['RE', 'UG', 'PI'];

const monedaForTip = (tip) => (EURO_TIPS.includes(String(tip || '').toUpperCase()) ? 'EUR' : 'LEI');

const formatMoney = (value, moneda) => `${Number(value || 0).toFixed(2)} ${moneda}`;

const emptyTotals = () => ({
  valoareCatalog: 0,
  valoareClientFinal: 0,
  valoareComisionar: 0,
  diferenta: 0
});

const addTotals = (acc, row) => ({
  valoareCatalog: acc.valoareCatalog + Number(row.valoareCatalog || 0),
  valoareClientFinal: acc.valoareClientFinal + Number(row.valoareClientFinal || 0),
  valoareComisionar: acc.valoareComisionar + Number(row.valoareComisionar || 0),
  diferenta: acc.diferenta + Number(row.diferenta || 0)
});

const PLATA_OP_PATTERNS = [
  /\bO\.?\s*P\.?\b/i,
  /\bordin\s+de\s+plat[aă]\b/i
];

/** Detecteaza in observatii sintagma OP (OP, O.P., O P, ordin de plata). */
const isPlataOpSauBanca = (observatii) => {
  const text = String(observatii || '').trim();
  if (!text || text === '-') {
    return false;
  }
  const normalized = text.replace(/\s+/g, ' ');
  return PLATA_OP_PATTERNS.some((pattern) => pattern.test(normalized));
};

const tipProdusLabel = (order) => {
  const tip = String(order.tipProdus || '').toUpperCase();
  const sub = String(order.subTip || '').toUpperCase();
  return sub ? `${tip}-${sub}` : tip || '-';
};

const buildRows = (orders) => {
  return (orders || []).map(order => {
    const valoareCatalog = Number(order.valoareCatalog || 0);
    const valoareClientFinal = Number(order.valoareClientFinal || 0);
    // "Valoare comisionar" = valoarea comenzii la pretul comisionarului.
    const valoareComisionar = Number(order.valoareComanda || 0);
    // "Diferenta" = comisionul (client final - comisionar).
    const diferenta = Number(
      order.valoareComisionar != null
        ? order.valoareComisionar
        : (valoareClientFinal - valoareComisionar)
    );
    const tip = String(order.tipProdus || '').toUpperCase().trim()
      || tipProdusLabel(order).slice(0, 2).toUpperCase();
    const observatiiRaw = String(order.observatii || '').trim();
    return {
      tip,
      tipProdus: tipProdusLabel(order),
      id: order.id,
      data: formatDateRO(order.dataComanda),
      client: order.clientClient || '-',
      valoareCatalog,
      valoareClientFinal,
      valoareComisionar,
      diferenta,
      observatii: observatiiRaw || '-',
      plataOpBanca: isPlataOpSauBanca(observatiiRaw)
    };
  });
};

const groupRowsByTipProdus = (rows) => {
  const groups = new Map();
  (rows || []).forEach((row) => {
    const key = row.tip || (row.tipProdus || '-').slice(0, 2).toUpperCase();
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(row);
  });
  return Array.from(groups.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([tipProdus, groupRows]) => {
      const sortedRows = [...groupRows].sort((a, b) => Number(a.id) - Number(b.id));
      const totals = sortedRows.reduce(addTotals, emptyTotals());
      const markedRows = sortedRows.filter((row) => row.plataOpBanca);
      const markedTotals = markedRows.reduce(addTotals, emptyTotals());
      return {
        tipProdus,
        moneda: monedaForTip(tipProdus),
        rows: sortedRows,
        totals,
        markedTotals,
        hasMarked: markedRows.length > 0
      };
    });
};

const computeGrandTotals = (groups) => {
  const result = { LEI: emptyTotals(), EUR: emptyTotals() };
  groups.forEach((group) => {
    result[group.moneda] = addTotals(result[group.moneda], group.totals);
  });
  return result;
};

const computeMarkedGrandTotals = (groups) => {
  const result = { LEI: emptyTotals(), EUR: emptyTotals() };
  groups.forEach((group) => {
    group.rows.forEach((row) => {
      if (row.plataOpBanca) {
        result[group.moneda] = addTotals(result[group.moneda], row);
      }
    });
  });
  return result;
};

const hasMarkedOrdersForMoneda = (groups, moneda) => groups.some(
  (group) => group.moneda === moneda && group.hasMarked
);

const TableHeader = () => (
  <View style={pdfStyles.headerRow}>
    <Text style={pdfStyles.cTip}>{t('reports.commissioner.columns.productType')}</Text>
    <Text style={pdfStyles.cId}>{t('reports.commissioner.columns.orderId')}</Text>
    <Text style={pdfStyles.cData}>{t('reports.commissioner.columns.date')}</Text>
    <Text style={pdfStyles.cClient}>{t('reports.commissioner.columns.client')}</Text>
    <Text style={pdfStyles.cVal}>{t('reports.commissioner.columns.catalogValue')}</Text>
    <Text style={pdfStyles.cValWide}>{t('reports.commissioner.columns.finalValue')}</Text>
    <Text style={pdfStyles.cValWide}>{t('reports.commissioner.columns.commissionerValue')}</Text>
    <Text style={pdfStyles.cVal}>{t('reports.commissioner.columns.difference')}</Text>
    <Text style={pdfStyles.cObs}>{t('reports.commissioner.columns.notes')}</Text>
    <Text style={pdfStyles.cMarkHeader}>{t('reports.commissioner.columns.op')}</Text>
  </View>
);

const ReportDocument = ({ rows, denumireComisionar, dataStart, dataEnd }) => {
  const groups = groupRowsByTipProdus(rows);
  const grandTotals = computeGrandTotals(groups);
  const markedGrandTotals = computeMarkedGrandTotals(groups);
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <Text style={pdfStyles.title}>{t('reports.commissioner.pdfTitle')}</Text>
        <Text style={pdfStyles.subtitle}>
          {t('reports.commissioner.companyPeriod', { name: denumireComisionar || '-', start: dataStart, end: dataEnd })}
        </Text>
        <Text style={pdfStyles.subtitle}>{t('reports.common.onlySentOrders')}</Text>
        <Text style={pdfStyles.subtitle}>
          {t('reports.commissioner.opInfo1')}
        </Text>
        <Text style={pdfStyles.subtitle}>
          {t('reports.commissioner.opInfo2')}
        </Text>

        {groups.map((group) => (
          <View key={group.tipProdus} style={pdfStyles.group}>
            <Text style={pdfStyles.groupTitle}>{t('reports.commissioner.productType', { type: group.tipProdus, currency: group.moneda })}</Text>
            <TableHeader />
            {group.rows.map((row, idx) => (
              <View key={`${row.id}-${idx}`} style={pdfStyles.row}>
                <Text style={pdfStyles.cTip}>{row.tipProdus}</Text>
                <Text style={pdfStyles.cId}>{String(row.id)}</Text>
                <Text style={pdfStyles.cData}>{row.data}</Text>
                <Text style={pdfStyles.cClient}>{row.client}</Text>
                <Text style={pdfStyles.cVal}>{formatNumber(row.valoareCatalog)}</Text>
                <Text style={pdfStyles.cValWide}>{formatNumber(row.valoareClientFinal)}</Text>
                <Text style={pdfStyles.cValWide}>{formatNumber(row.valoareComisionar)}</Text>
                <Text style={pdfStyles.cVal}>{formatNumber(row.diferenta)}</Text>
                <Text style={pdfStyles.cObsValue}>{row.observatii}</Text>
                <Text style={row.plataOpBanca ? pdfStyles.cMarkActive : pdfStyles.cMark}>
                  {row.plataOpBanca ? t('reports.commissioner.opMark') : ''}
                </Text>
              </View>
            ))}
            <View style={pdfStyles.totalRow}>
              <Text style={pdfStyles.cTip}>{t('reports.commissioner.totalByType', { type: group.tipProdus })}</Text>
              <Text style={pdfStyles.cId} />
              <Text style={pdfStyles.cData} />
              <Text style={pdfStyles.cClient} />
              <Text style={pdfStyles.cVal}>{formatMoney(group.totals.valoareCatalog, group.moneda)}</Text>
              <Text style={pdfStyles.cValWide}>{formatMoney(group.totals.valoareClientFinal, group.moneda)}</Text>
              <Text style={pdfStyles.cValWide}>{formatMoney(group.totals.valoareComisionar, group.moneda)}</Text>
              <Text style={pdfStyles.cVal}>{formatMoney(group.totals.diferenta, group.moneda)}</Text>
              <Text style={pdfStyles.cObs} />
              <Text style={pdfStyles.cMark} />
            </View>
            {group.hasMarked && (
              <View style={pdfStyles.markedTotalRow}>
                <Text style={pdfStyles.cTip}>{t('reports.commissioner.totalOpByType', { type: group.tipProdus })}</Text>
                <Text style={pdfStyles.cId} />
                <Text style={pdfStyles.cData} />
                <Text style={pdfStyles.cClient} />
                <Text style={pdfStyles.cVal}>{formatMoney(group.markedTotals.valoareCatalog, group.moneda)}</Text>
                <Text style={pdfStyles.cValWide}>{formatMoney(group.markedTotals.valoareClientFinal, group.moneda)}</Text>
                <Text style={pdfStyles.cValWide}>{formatMoney(group.markedTotals.valoareComisionar, group.moneda)}</Text>
                <Text style={pdfStyles.cVal}>{formatMoney(group.markedTotals.diferenta, group.moneda)}</Text>
                <Text style={pdfStyles.cObs} />
                <Text style={pdfStyles.cMark} />
              </View>
            )}
          </View>
        ))}

        <View style={pdfStyles.grandGroup}>
          <Text style={pdfStyles.groupTitle}>{t('reports.commissioner.totalGeneral')}</Text>
          {['LEI', 'EUR'].map((moneda) => (
            <View key={moneda} style={pdfStyles.grandTotalRow}>
              <Text style={pdfStyles.cTip}>{t('reports.commissioner.totalByCurrency', { currency: moneda })}</Text>
              <Text style={pdfStyles.cId} />
              <Text style={pdfStyles.cData} />
              <Text style={pdfStyles.cClient} />
              <Text style={pdfStyles.cVal}>{formatMoney(grandTotals[moneda].valoareCatalog, moneda)}</Text>
              <Text style={pdfStyles.cValWide}>{formatMoney(grandTotals[moneda].valoareClientFinal, moneda)}</Text>
              <Text style={pdfStyles.cValWide}>{formatMoney(grandTotals[moneda].valoareComisionar, moneda)}</Text>
              <Text style={pdfStyles.cVal}>{formatMoney(grandTotals[moneda].diferenta, moneda)}</Text>
              <Text style={pdfStyles.cObs} />
              <Text style={pdfStyles.cMark} />
            </View>
          ))}
          {['LEI', 'EUR'].map((moneda) => (
            hasMarkedOrdersForMoneda(groups, moneda) ? (
              <View key={`marked-${moneda}`} style={pdfStyles.markedTotalRow}>
                <Text style={pdfStyles.cTip}>{t('reports.commissioner.totalOpByCurrency', { currency: moneda })}</Text>
                <Text style={pdfStyles.cId} />
                <Text style={pdfStyles.cData} />
                <Text style={pdfStyles.cClient} />
                <Text style={pdfStyles.cVal}>{formatMoney(markedGrandTotals[moneda].valoareCatalog, moneda)}</Text>
                <Text style={pdfStyles.cValWide}>{formatMoney(markedGrandTotals[moneda].valoareClientFinal, moneda)}</Text>
                <Text style={pdfStyles.cValWide}>{formatMoney(markedGrandTotals[moneda].valoareComisionar, moneda)}</Text>
                <Text style={pdfStyles.cVal}>{formatMoney(markedGrandTotals[moneda].diferenta, moneda)}</Text>
                <Text style={pdfStyles.cObs} />
                <Text style={pdfStyles.cMark} />
              </View>
            ) : null
          ))}
        </View>
      </Page>
    </Document>
  );
};

ReportDocument.propTypes = {
  rows: PropTypes.array,
  denumireComisionar: PropTypes.string,
  dataStart: PropTypes.string,
  dataEnd: PropTypes.string
};

const CommissionerReport = ({ currentUser, firmeComisionar }) => {
  const classes = useStyles();
  const [dataStart, setDataStart] = useState(() => getMonthStartRO());
  const [dataEnd, setDataEnd] = useState(() => getTodayRO());
  const [isGenerating, setIsGenerating] = useState(false);
  const isComisionar = currentUser?.idFirma?.cfg1 === 'DA';
  const clientiIds = useMemo(() => (firmeComisionar || []).map(f => f.id), [firmeComisionar]);

  if (!isComisionar) {
    return null;
  }

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    if (!dataStart || !dataEnd) {
      window.alert(t('reports.common.periodRequired'));
      return;
    }
    const start = parseRoDate(dataStart, false);
    const end = parseRoDate(dataEnd, true);
    if (!start || !end) {
      window.alert(t('reports.common.invalidDateFormat'));
      return;
    }
    if (start > end) {
      window.alert(t('reports.common.startAfterEnd'));
      return;
    }
    if (clientiIds.length === 0) {
      window.alert(t('reports.commissioner.noClients'));
      return;
    }
    setIsGenerating(true);
    try {
      const orders = await strapi.getEntries('comandas', {
        idFirma_in: clientiIds,
        stadiu: 'TRANSMISA',
        _limit: -1,
        _sort: 'dataComanda:ASC'
      });
      const filtered = (orders || []).filter(order => {
        const d = new Date(order.dataComanda);
        return !Number.isNaN(d.getTime()) && d >= start && d <= end;
      });
      const rows = buildRows(filtered);
      const blob = await pdf(
        <ReportDocument
          rows={rows}
          denumireComisionar={currentUser?.idFirma?.denumire || ''}
          dataStart={dataStart}
          dataEnd={dataEnd}
        />
      ).toBlob();
      const fileStart = String(dataStart).replace(/\./g, '-');
      const fileEnd = String(dataEnd).replace(/\./g, '-');
      downloadBlob(blob, `${t('reports.commissioner.filePrefix')}-${fileStart}-${fileEnd}.pdf`);
    } catch (e) {
      window.alert(t('reports.common.generateError', { error: e }));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader className={classes.header} title={t('reports.commissioner.cardTitle')} />
      <Divider />
      <CardContent>
        <div style={{ marginBottom: 8, color: '#455A64', fontWeight: 600 }}>
          {t('reports.common.onlySentOrders')}
        </div>
        <Grid container spacing={2}>
          <Grid item md={2} xs={12}>
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              label={t('reports.common.dateStart')}
              margin="dense"
              onChange={(e) => setDataStart(e.target.value)}
              placeholder={t('reports.common.dateFormatPlaceholder')}
              type="text"
              value={dataStart}
              variant="outlined"
            />
          </Grid>
          <Grid item md={2} xs={12}>
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              label={t('reports.common.dateEnd')}
              margin="dense"
              onChange={(e) => setDataEnd(e.target.value)}
              placeholder={t('reports.common.dateFormatPlaceholder')}
              type="text"
              value={dataEnd}
              variant="outlined"
            />
          </Grid>
          <Grid item md={8} xs={12} style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              className={classes.button}
              color="primary"
              disabled={isGenerating}
              onClick={handleGenerate}
              startIcon={<GetAppIcon />}
              variant="contained"
            >
              {t('reports.commissioner.button')}
            </Button>
            {isGenerating && (
              <div style={{ marginLeft: 12 }}>
                <Loader color="#00BFFF" height={22} type="Puff" width={22} />
              </div>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

CommissionerReport.propTypes = {
  currentUser: PropTypes.object,
  firmeComisionar: PropTypes.array
};

const mapStateToProps = state => ({
  currentUser: selectCurrentUser(state),
  firmeComisionar: selectLookupFirmeComisionar(state)
});

export default connect(mapStateToProps)(CommissionerReport);
